import _ from 'lodash';
import { v4 as uuid } from 'uuid';
import { useApolloClient } from '@apollo/client';
import { Item } from '@baemingo/table-events/dist/types';
import { useTableApi, useTableUniverse } from '~Components/TableUniverse';
import { useInventoryApi, useInventoryUniverse } from '~Components/InventoryUniverse';
import { useEntryFinder } from '~Components/EntryFinder';
import { useCompetition } from '~Providers/CompetitionsProvider';
import { useI18n, useLogger, useMetadata, useSelector } from '~Hooks';
import useOffers from '~Hooks/useOffers';
import { packageItems } from '~Utils/packagedItems';
import { CartItem, LocalItem, LocalVariation } from '~Types';

interface DuplicateArgs {
  /** The cheque line being copied */
  sourceItem: Item;
  /** How many copies to add */
  count: number;
  variations: LocalVariation[];
  removedVariations: LocalVariation[];
  chequeId: string;
  tableId: string;
  tableName: string;
}

/**
 * Adds copies of an existing cheque line with the same side effects as bonging
 * from the order screen: kitchen bong print, inventory, competitions and log.
 */
export default function useAddChequeItems() {
  const api = useTableApi();
  const universe = useTableUniverse();
  const client = useApolloClient();
  const { i18n } = useI18n();
  const logger = useLogger('add-cheque-items');
  const findEntryFromItem = useEntryFinder();
  const metadata = useMetadata();
  const offers = useOffers();
  const competitions = useCompetition();
  const inventoryUniverse = useInventoryUniverse();

  const staff = useSelector((state) => state.staff);
  const venueId = useSelector((state) => state.venueId);
  const inventoryApi = useInventoryApi(venueId);

  /** Returns the ids of the added copies */
  const duplicate = ({
    sourceItem,
    count,
    variations,
    removedVariations,
    chequeId,
    tableId,
    tableName,
  }: DuplicateArgs): string[] => {
    const cheque = universe.cheques.find((c) => c.id === chequeId);
    const entryId = findEntryFromItem(sourceItem.itemId).id;
    const fraction = sourceItem.fraction || 1;

    // Built explicitly (rather than spreading `sourceItem`) since `sourceItem`
    // is a runtime `Item` with several fields that don't exist on `LocalItem`
    const localItems: LocalItem[] = _.range(count).map(() => ({
      id: uuid(),
      itemId: sourceItem.itemId,
      categoryId: sourceItem.categoryId,
      title: sourceItem.title,
      description: '',
      amount: sourceItem.amount,
      discount: sourceItem.discount,
      fraction,
      vatRate: sourceItem.vatRate,
      message: sourceItem.message,
      manualName: sourceItem.manualName,
      disableMembershipDiscount: sourceItem.disableMembershipDiscount,
      entryId,
      // addItems reads course info from `type`, not the runtime `courseId`
      type: sourceItem.courseId ?? undefined,
      variations,
      removedVariations,
    }));

    api.addItems(localItems, chequeId);

    // Per-unit base price of the source line, so the packaged copies get the
    // same amount as the line they were copied from (also for manual prices)
    const unitPrice = sourceItem.amount / fraction - _.sum(variations.map((v) => v.price));

    /** Packaged like the order screen does, since bong/competitions/log expect CartItems */
    let packagedItems: CartItem[] = [];

    try {
      packagedItems = packageItems(
        localItems.map((item) => ({ ...item, amount: undefined, price: unitPrice })),
        venueId ?? '',
        null,
        null,
        findEntryFromItem,
        client,
        i18n,
        metadata.data.discountExcludedCategoryIds,
        undefined,
        undefined,
        undefined,
        offers.offers,
      );
    } catch (e) {
      logger.error('Failed to package duplicated items', { itemId: sourceItem.itemId, count });
    }

    packagedItems.forEach((item) => {
      const invItem = inventoryUniverse.items.find((i) => i.itemId === item.itemId);

      if (invItem && typeof invItem.count === 'number') {
        inventoryApi.reduceStock(invItem.itemId, item.fraction, 'Order');
      }
    });

    if (staff?.id) {
      competitions.addSale({ staffId: staff.id, items: packagedItems });
    }

    logger.info('Sending kitchen bong for duplicated items', {
      chequeId,
      tableName,
      count,
      itemId: sourceItem.itemId,
    });

    api.sendKitchenBong(
      [],
      packagedItems,
      tableName,
      tableName,
      tableId,
      cheque?.coverage ?? undefined,
    );

    api.log.addItems(chequeId, tableId, packagedItems);

    return localItems.map((item) => item.id);
  };

  return { duplicate };
}
